import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Lock, User, AlertCircle, Eye, EyeOff, TrendingUp, X } from 'lucide-react';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      setShowToast(true);
      const timer = setTimeout(() => {
        setShowToast(false);
        setError('');
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setShowToast(false);
    setLoading(true);

    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 12px'
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
    padding: '20px',
    width: '100%',
    maxWidth: '360px'
  };

  const logoStyle: React.CSSProperties = {
    height: '40px',
    width: '40px',
    background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
    boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px 8px 36px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: '#f9fafb',
    transition: 'all 0.2s',
    outline: 'none'
  };

  const inputFocusStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: '#2563eb',
    backgroundColor: 'white',
    boxShadow: '0 0 0 2px rgba(37, 99, 235, 0.1)'
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    background: 'linear-gradient(135deg, #2563eb, #4f46e5)',
    color: 'white',
    padding: '10px 16px',
    borderRadius: '6px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)',
    fontSize: '14px'
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px'
  };

  const featureCardStyle: React.CSSProperties = {
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(10px)',
    borderRadius: '6px',
    padding: '10px',
    textAlign: 'center' as const,
    border: '1px solid rgba(255, 255, 255, 0.3)'
  };

  return (
    <div style={containerStyle}>
      {/* Toast Notification */}
      {showToast && error && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 9999,
          background: '#fff',
          border: '1px solid #fecaca',
          borderLeft: '4px solid #ef4444',
          borderRadius: '8px',
          padding: '14px 16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minWidth: '280px',
          maxWidth: '360px',
          animation: 'slideIn 0.3s ease'
        }}>
          <AlertCircle size={18} color="#ef4444" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#b91c1c', fontWeight: '500', flex: 1 }}>{error}</span>
          <button
            onClick={() => { setShowToast(false); setError(''); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9ca3af' }}
          >
            <X size={16} />
          </button>
        </div>
      )}
      <div style={{ maxWidth: '400px', width: '100%' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={logoStyle}>
            <TrendingUp size={20} color="white" />
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: '600', color: '#111827', margin: '0 0 4px' }}>
            Auto Trade Platform
          </h1>
          <p style={{ color: '#6b7280', margin: 0, fontSize: '13px' }}>
            Professional Trading Solutions
          </p>
        </div>

        {/* Login Card */}
        <div style={cardStyle}>
          <div style={{ marginBottom: '16px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 4px' }}>Welcome Back</h2>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '13px' }}>Sign in to access your trading dashboard</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Username Field */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Username
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <User size={16} color="#9ca3af" />
                </div>
                <input
                  type="text"
                  required
                  style={inputStyle}
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
                  onBlur={(e) => Object.assign(e.target.style, inputStyle)}
                />
              </div>
            </div>

            {/* Password Field */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <Lock size={16} color="#9ca3af" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  style={{ ...inputStyle, paddingRight: '36px' }}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={(e) => Object.assign(e.target.style, { ...inputFocusStyle, paddingRight: '36px' })}
                  onBlur={(e) => Object.assign(e.target.style, { ...inputStyle, paddingRight: '36px' })}
                />
                <button
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff size={16} color="#9ca3af" />
                  ) : (
                    <Eye size={16} color="#9ca3af" />
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                ...buttonStyle,
                opacity: loading ? 0.5 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginRight: '6px'
                  }}></div>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          {/* <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '12px' }}>
              Don't have an account?{' '}
              <Link
                to="/register"
                style={{ color: '#2563eb', textDecoration: 'none', fontWeight: '500' }}
              >
                Create Account
              </Link>
            </p>
          </div> */}
        </div>

        {/* Features */}
        <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          <div style={featureCardStyle}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#2563eb' }}>24/7</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>Trading</div>
          </div>
          <div style={featureCardStyle}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#16a34a' }}>Real-time</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>Data</div>
          </div>
          <div style={featureCardStyle}>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#7c3aed' }}>Secure</div>
            <div style={{ fontSize: '11px', color: '#6b7280' }}>Platform</div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Login;